import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Bed, Maximize, Plus, Check, Heart, Flame, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCompare } from "@/context/CompareContext";
import { useFavorites } from "@/context/FavoritesContext";

const CURRENCY_PREFIX = { INR: "₹", AED: "AED ", SGD: "S$", USD: "$" };

export function formatPrice(price, currency = "INR") {
  if (price == null) return "—";
  const prefix = CURRENCY_PREFIX[currency] || "";
  if (currency === "INR") {
    if (price >= 10000000) return `${prefix}${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `${prefix}${(price / 100000).toFixed(2)} L`;
  }
  if (price >= 1000000) return `${prefix}${(price / 1000000).toFixed(2)} M`;
  if (price >= 1000) return `${prefix}${(price / 1000).toFixed(0)}K`;
  return `${prefix}${price}`;
}

export default function PropertyCard({ project, onCompareClick }) {
  const { add, remove, has } = useCompare();
  const { has: isFav, toggle: toggleFav } = useFavorites();
  const checked = has(project.id);
  const fav = isFav(project.id);
  const img =
    project.images?.[0] ||
    "https://images.unsplash.com/photo-1582268611958-ebfd161df9d8?w=1200";

  const discountedPrice = project.discount_pct
    ? Math.round(project.price_min * (1 - project.discount_pct / 100))
    : null;

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (checked) remove(project.id);
    else add(project);
    if (onCompareClick) onCompareClick();
  };

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggleFav(project.id);
    if (res?.needsAuth) {
      toast("Sign in to save favorites", { description: "Create a free account or login to save projects to your shortlist." });
    }
  };

  return (
    <Link
      to={`/projects/${project.id}`}
      data-testid={`project-card-${project.id}`}
      className="group block bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={img}
          alt={project.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Top-left: discount + status badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
          {project.hot_deal && project.discount_pct && (
            <span className="bg-[#EF4444] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md flex items-center gap-1">
              <Flame className="w-3 h-3" fill="currentColor" />
              {project.discount_pct}% OFF
            </span>
          )}
          {project.featured && !project.hot_deal && (
            <span className="bg-[#D4A437] text-white text-[10px] tracking-wider uppercase font-bold px-2.5 py-1 rounded shadow-sm">
              Featured
            </span>
          )}
          <span className="bg-white/95 backdrop-blur-sm text-slate-700 text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded shadow-sm border border-slate-100">
            {project.status?.replace("_", " ")}
          </span>
        </div>

        {/* Top-right: actions */}
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={handleFav}
            data-testid={`favorite-toggle-${project.id}`}
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all shadow-md ${
              fav
                ? "bg-[#EF4444] text-white"
                : "bg-white/95 text-slate-600 hover:text-[#EF4444]"
            }`}
            title="Save"
          >
            <Heart className="w-4 h-4" strokeWidth={2} fill={fav ? "currentColor" : "none"} />
          </button>
          <button
            onClick={handleCompare}
            data-testid={`compare-toggle-${project.id}`}
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all shadow-md ${
              checked
                ? "bg-[#D4A437] text-white"
                : "bg-white/95 text-slate-600 hover:text-[#D4A437]"
            }`}
            title="Compare"
          >
            {checked ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <Plus className="w-4 h-4" strokeWidth={2} />}
          </button>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1.5 text-[#B88C2E] text-xs font-medium">
            <MapPin className="w-3 h-3" strokeWidth={2} />
            <span className="truncate">{project.locality}, {project.city}</span>
          </div>
          {project.rera && (
            <span className="rera-badge shrink-0">
              <ShieldCheck className="w-3 h-3" strokeWidth={2} /> RERA
            </span>
          )}
        </div>

        <h3 className="font-serif text-lg font-bold text-slate-900 mb-1 group-hover:text-[#D4A437] transition-colors line-clamp-1">
          {project.name}
        </h3>
        <p className="text-xs text-slate-500 mb-4">By {project.developer}</p>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {project.bhk?.length > 0 && (
            <span className="pill-bhk">
              <Bed className="w-3 h-3 mr-1" strokeWidth={2} />
              {project.bhk.join(", ")} BHK
            </span>
          )}
          <span className="pill-bhk bg-slate-100 text-slate-600">
            <Maximize className="w-3 h-3 mr-1" strokeWidth={2} />
            {project.area_min}–{project.area_max} sqft
          </span>
        </div>

        <div className="flex items-end justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="text-[10px] tracking-wider text-slate-400 uppercase font-semibold">Starting</div>
            {discountedPrice ? (
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-[#D4A437]">{formatPrice(discountedPrice, project.currency)}</div>
                <div className="text-xs text-slate-400 line-through">{formatPrice(project.price_min, project.currency)}</div>
              </div>
            ) : (
              <div className="text-2xl font-bold text-[#D4A437]">{formatPrice(project.price_min, project.currency)}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-700">{project.price_per_sqft?.toLocaleString()}</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">per sqft</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
